/**
 * Shared PDF Generation Utilities for Assessment Tools
 *
 * This module provides reusable PDF generation functions for all assessment tools
 * including maturity assessments, compliance checklists, readiness assessments, etc.
 *
 * Uses jsPDF and jspdf-autotable for consistent, professional PDF reports.
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Extended jsPDF type to include autoTable plugin
 */
export interface jsPDFWithPlugin extends jsPDF {
  lastAutoTable: {
    finalY: number;
  };
}

/**
 * Summary metric for executive summary sections
 */
export interface SummaryMetric {
  label: string;
  value: string | number;
  highlighted?: boolean; // If true, formats as bold/colored
}

/**
 * Options for table generation
 */
export interface TableOptions {
  theme?: 'striped' | 'grid' | 'plain';
  headColor?: [number, number, number]; // RGB color for header
  fontSize?: number;
  columnWidths?: { [key: number]: number };
  columnAligns?: { [key: number]: 'left' | 'center' | 'right' };
  boldColumns?: number[]; // Column indices to make bold
}

/**
 * Options for section headers
 */
export interface SectionOptions {
  color?: [number, number, number]; // RGB color
  fontSize?: number;
  underline?: boolean;
  addSpace?: boolean; // Add spacing before section
}

// ============================================================================
// COLOR CONSTANTS
// ============================================================================

export const COLORS = {
  // Primary brand colors
  BLUE_600: [37, 99, 235] as [number, number, number],
  BLUE_500: [59, 130, 246] as [number, number, number],
  BLUE_50: [239, 246, 255] as [number, number, number],

  // Status colors
  GREEN_600: [22, 163, 74] as [number, number, number],
  GREEN_500: [34, 197, 94] as [number, number, number],
  GREEN_100: [220, 252, 231] as [number, number, number],

  YELLOW_600: [202, 138, 4] as [number, number, number],
  YELLOW_500: [234, 179, 8] as [number, number, number],
  YELLOW_100: [254, 249, 195] as [number, number, number],

  RED_600: [220, 38, 38] as [number, number, number],
  RED_500: [239, 68, 68] as [number, number, number],
  RED_100: [254, 226, 226] as [number, number, number],

  ORANGE_500: [249, 115, 22] as [number, number, number],

  // Neutral colors
  GRAY_900: [17, 24, 39] as [number, number, number],
  GRAY_800: [31, 41, 55] as [number, number, number],
  GRAY_700: [55, 65, 81] as [number, number, number],
  GRAY_600: [75, 85, 99] as [number, number, number],
  GRAY_500: [107, 114, 128] as [number, number, number],
  GRAY_400: [156, 163, 175] as [number, number, number],
  GRAY_300: [209, 213, 219] as [number, number, number],
  GRAY_200: [229, 231, 235] as [number, number, number],
  GRAY_100: [243, 244, 246] as [number, number, number],
  GRAY_50: [249, 250, 251] as [number, number, number],

  WHITE: [255, 255, 255] as [number, number, number],
  BLACK: [0, 0, 0] as [number, number, number],
};

// ============================================================================
// CORE UTILITIES
// ============================================================================

/**
 * Create a new jsPDF instance with standard configuration
 *
 * @returns Configured jsPDF instance
 */
export function createAssessmentPDF(): jsPDFWithPlugin {
  return new jsPDF() as jsPDFWithPlugin;
}

/**
 * Get page dimensions for layout calculations
 *
 * @param doc - jsPDF instance
 * @returns Object with width and height
 */
export function getPageDimensions(doc: jsPDF): { width: number; height: number } {
  return {
    width: doc.internal.pageSize.width,
    height: doc.internal.pageSize.height,
  };
}

/**
 * Format a date as "Month DD, YYYY"
 *
 * @param date - Date object (defaults to current date)
 * @returns Formatted date string
 */
export function formatDate(date: Date = new Date()): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a currency value
 *
 * @param value - Numeric value
 * @param includeDecimals - Whether to include cents (default: false)
 * @returns Formatted currency string
 */
export function formatCurrency(value: number, includeDecimals = false): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: includeDecimals ? 2 : 0,
    maximumFractionDigits: includeDecimals ? 2 : 0,
  }).format(value);
}

/**
 * Format industry name (converts snake_case to Title Case)
 *
 * @param industry - Industry identifier
 * @returns Formatted industry name
 */
export function formatIndustry(industry: string): string {
  return industry.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Check if we need a new page based on current Y position
 *
 * @param doc - jsPDF instance
 * @param currentY - Current Y position
 * @param requiredSpace - Space needed for next content
 * @returns New Y position (either on same page or new page)
 */
export function checkPageBreak(
  doc: jsPDFWithPlugin,
  currentY: number,
  requiredSpace = 50
): number {
  const { height } = getPageDimensions(doc);
  const bottomMargin = 30; // Leave space for page numbers/footer

  if (currentY + requiredSpace > height - bottomMargin) {
    doc.addPage();
    return 20; // Top margin for new page
  }

  return currentY;
}

// ============================================================================
// TITLE PAGE
// ============================================================================

/**
 * Add a professional title page with Inventive HQ branding
 *
 * @param doc - jsPDF instance
 * @param title - Main title (e.g., "Cybersecurity Maturity")
 * @param subtitle - Subtitle (e.g., "Assessment Report")
 * @param description - Short description (e.g., "Strategic Security Roadmap")
 * @param details - Key-value pairs for the details box
 * @param highlightLabel - Label for highlighted metric (e.g., "Overall Maturity Level")
 * @param highlightValue - Value for highlighted metric (e.g., "LEVEL 3 - DEFINED")
 * @returns Final Y position
 */
export function addTitlePage(
  doc: jsPDFWithPlugin,
  title: string,
  subtitle: string,
  description: string,
  details: { [key: string]: string },
  highlightLabel?: string,
  highlightValue?: string
): number {
  const { width, height } = getPageDimensions(doc);
  let yPos = 50;

  // Company branding
  doc.setFontSize(20);
  doc.setTextColor(...COLORS.BLUE_600);
  doc.setFont('helvetica', 'bold');
  doc.text('INVENTIVE HQ', width / 2, yPos, { align: 'center' });

  // Main Title
  yPos += 20;
  doc.setFontSize(28);
  doc.setTextColor(...COLORS.GRAY_900);
  doc.text(title, width / 2, yPos, { align: 'center' });

  yPos += 12;
  doc.text(subtitle, width / 2, yPos, { align: 'center' });

  // Description
  yPos += 16;
  doc.setFontSize(18);
  doc.setTextColor(...COLORS.GRAY_600);
  doc.setFont('helvetica', 'normal');
  doc.text(description, width / 2, yPos, { align: 'center' });

  // Details Box
  yPos += 30;
  const detailsHeight = Object.keys(details).length * 10 + 12;
  doc.setFillColor(...COLORS.GRAY_50);
  doc.roundedRect(30, yPos, width - 60, detailsHeight, 3, 3, 'F');

  yPos += 12;
  doc.setFontSize(12);
  doc.setTextColor(...COLORS.GRAY_900);

  Object.entries(details).forEach(([key, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(`${key}:`, 40, yPos);
    doc.setFont('helvetica', 'normal');
    doc.text(value, 100, yPos);
    yPos += 10;
  });

  // Highlight box (if provided)
  if (highlightLabel && highlightValue) {
    yPos += 15;
    doc.setDrawColor(...COLORS.BLUE_600);
    doc.setLineWidth(2);
    doc.roundedRect(30, yPos, width - 60, 35, 3, 3, 'S');

    yPos += 12;
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.GRAY_600);
    doc.setFont('helvetica', 'normal');
    doc.text(highlightLabel, width / 2, yPos, { align: 'center' });

    yPos += 10;
    doc.setFontSize(24);
    doc.setTextColor(...COLORS.BLUE_600);
    doc.setFont('helvetica', 'bold');
    doc.text(highlightValue, width / 2, yPos, { align: 'center' });
  }

  // Footer
  const footerY = height - 30;
  doc.setFontSize(9);
  doc.setTextColor(...COLORS.GRAY_500);
  doc.setFont('helvetica', 'normal');
  doc.text('Based on industry-standard frameworks', width / 2, footerY, { align: 'center' });
  doc.text('and best practice security controls', width / 2, footerY + 4, { align: 'center' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Confidential - For Internal Use Only', width / 2, footerY + 10, { align: 'center' });

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.GRAY_400);
  doc.setFont('helvetica', 'normal');
  doc.text(
    'inventivehq.com  |  (866) 903-2097  |  info@inventivehq.com',
    width / 2,
    footerY + 16,
    { align: 'center' }
  );

  return yPos;
}

// ============================================================================
// EXECUTIVE SUMMARY
// ============================================================================

/**
 * Add executive summary section with key metrics
 *
 * @param doc - jsPDF instance
 * @param title - Section title (default: "Executive Summary")
 * @param metrics - Array of summary metrics
 * @param startY - Starting Y position (default: 20)
 * @returns Final Y position
 */
export function addExecutiveSummary(
  doc: jsPDFWithPlugin,
  metrics: SummaryMetric[],
  title = 'Executive Summary',
  startY = 20
): number {
  const { width } = getPageDimensions(doc);
  let yPos = startY;

  // Header
  doc.setFontSize(24);
  doc.setTextColor(...COLORS.BLUE_600);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 20, yPos);

  yPos += 10;
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.GRAY_500);
  doc.setFont('helvetica', 'normal');
  doc.text('Generated by Inventive HQ | inventivehq.com', 20, yPos);

  yPos += 3;
  doc.setTextColor(...COLORS.GRAY_400);
  doc.text(`Report Date: ${formatDate()}`, 20, yPos);

  yPos += 15;

  // Key Metrics Table
  doc.setFontSize(14);
  doc.setTextColor(...COLORS.GRAY_900);
  doc.setFont('helvetica', 'bold');
  doc.text('Key Metrics', 20, yPos);
  yPos += 8;

  const metricsData = metrics.map((metric) => {
    const value = typeof metric.value === 'number' ? metric.value.toString() : metric.value;
    return [metric.label, value];
  });

  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Value']],
    body: metricsData,
    theme: 'grid',
    headStyles: {
      fillColor: COLORS.BLUE_600,
      textColor: 255,
      fontStyle: 'bold',
    },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' },
    },
  });

  return doc.lastAutoTable.finalY;
}

// ============================================================================
// SCORE VISUALIZATION
// ============================================================================

/**
 * Add a circular score card visualization
 *
 * @param doc - jsPDF instance
 * @param score - Score value (0-100)
 * @param label - Label text
 * @param x - X position for center
 * @param y - Y position for center
 * @param radius - Circle radius (default: 30)
 * @param color - Score color (default: BLUE_600)
 * @returns Final Y position
 */
export function addScoreCard(
  doc: jsPDF,
  score: number,
  label: string,
  x: number,
  y: number,
  radius = 30,
  color: [number, number, number] = COLORS.BLUE_600
): number {
  // Outer circle (background)
  doc.setFillColor(...COLORS.GRAY_100);
  doc.circle(x, y, radius, 'F');

  // Score arc (if score > 0)
  if (score > 0) {
    doc.setFillColor(...color);
    // Draw filled arc for score percentage
    const startAngle = -90; // Start at top
    const endAngle = startAngle + (score / 100) * 360;

    // Note: jsPDF doesn't have native arc fill, so we'll use a solid circle
    // For actual arc visualization, consider using canvas and converting to image
    const scoreRadius = radius * 0.85;
    doc.circle(x, y, scoreRadius, 'F');
  }

  // Inner white circle for score text
  const innerRadius = radius * 0.65;
  doc.setFillColor(...COLORS.WHITE);
  doc.circle(x, y, innerRadius, 'F');

  // Score text
  doc.setFontSize(20);
  doc.setTextColor(...COLORS.GRAY_900);
  doc.setFont('helvetica', 'bold');
  doc.text(score.toFixed(0), x, y + 2, { align: 'center' });

  // Label below circle
  const labelY = y + radius + 8;
  doc.setFontSize(10);
  doc.setTextColor(...COLORS.GRAY_600);
  doc.setFont('helvetica', 'normal');
  doc.text(label, x, labelY, { align: 'center' });

  return labelY + 5;
}

// ============================================================================
// TABLES
// ============================================================================

/**
 * Add a formatted table with consistent styling
 *
 * @param doc - jsPDF instance
 * @param headers - Array of header labels
 * @param rows - Array of row data
 * @param startY - Starting Y position
 * @param options - Table formatting options
 * @returns Final Y position
 */
export function addTable(
  doc: jsPDFWithPlugin,
  headers: string[],
  rows: (string | number)[][],
  startY: number,
  options: TableOptions = {}
): number {
  const {
    theme = 'grid',
    headColor = COLORS.BLUE_600,
    fontSize = 10,
    columnWidths = {},
    columnAligns = {},
    boldColumns = [],
  } = options;

  // Build column styles
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columnStyles: Record<number, any> = {};

  Object.entries(columnWidths).forEach(([colIndex, width]) => {
    const index = parseInt(colIndex);
    columnStyles[index] = { ...columnStyles[index], cellWidth: width };
  });

  Object.entries(columnAligns).forEach(([colIndex, align]) => {
    const index = parseInt(colIndex);
    columnStyles[index] = { ...columnStyles[index], halign: align };
  });

  boldColumns.forEach((index) => {
    columnStyles[index] = { ...columnStyles[index], fontStyle: 'bold' };
  });

  autoTable(doc, {
    startY,
    head: [headers],
    body: rows,
    theme,
    headStyles: {
      fillColor: headColor,
      textColor: 255,
      fontStyle: 'bold',
    },
    styles: { fontSize },
    columnStyles,
  });

  return doc.lastAutoTable.finalY;
}

// ============================================================================
// SECTION HEADERS
// ============================================================================

/**
 * Add a section header with consistent formatting
 *
 * @param doc - jsPDF instance
 * @param title - Section title
 * @param yPos - Y position
 * @param options - Section formatting options
 * @returns New Y position after header
 */
export function addSection(
  doc: jsPDF,
  title: string,
  yPos: number,
  options: SectionOptions = {}
): number {
  const {
    color = COLORS.BLUE_600,
    fontSize = 20,
    underline = false,
    addSpace = true,
  } = options;

  let currentY = yPos;

  if (addSpace) {
    currentY += 10;
  }

  doc.setFontSize(fontSize);
  doc.setTextColor(...color);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 20, currentY);

  if (underline) {
    const textWidth = doc.getTextWidth(title);
    doc.setDrawColor(...color);
    doc.setLineWidth(0.5);
    doc.line(20, currentY + 2, 20 + textWidth, currentY + 2);
  }

  return currentY + 12;
}

/**
 * Add a subsection header (smaller than main section)
 *
 * @param doc - jsPDF instance
 * @param title - Subsection title
 * @param yPos - Y position
 * @param color - Text color (default: GRAY_900)
 * @returns New Y position after header
 */
export function addSubsection(
  doc: jsPDF,
  title: string,
  yPos: number,
  color: [number, number, number] = COLORS.GRAY_900
): number {
  doc.setFontSize(14);
  doc.setTextColor(...color);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 20, yPos);

  return yPos + 8;
}

// ============================================================================
// CONTENT BOXES
// ============================================================================

/**
 * Add a highlighted content box
 *
 * @param doc - jsPDF instance
 * @param content - Array of content lines (each line is { label?: string, text: string })
 * @param x - X position
 * @param y - Y position
 * @param width - Box width
 * @param bgColor - Background color (default: BLUE_500)
 * @param textColor - Text color (default: WHITE)
 * @returns Final Y position
 */
export function addContentBox(
  doc: jsPDF,
  content: Array<{ label?: string; text: string }>,
  x: number,
  y: number,
  width: number,
  bgColor: [number, number, number] = COLORS.BLUE_500,
  textColor: [number, number, number] = COLORS.WHITE
): number {
  const lineHeight = 5;
  const padding = 8;
  const height = content.length * lineHeight + padding * 2;

  // Draw box
  doc.setFillColor(...bgColor);
  doc.roundedRect(x, y, width, height, 3, 3, 'F');

  // Add content
  let yPos = y + padding;
  doc.setFontSize(10);
  doc.setTextColor(...textColor);

  content.forEach((line) => {
    if (line.label) {
      doc.setFont('helvetica', 'bold');
      doc.text(`${line.label}: `, x + padding, yPos);
      doc.setFont('helvetica', 'normal');
      doc.text(line.text, x + padding + doc.getTextWidth(`${line.label}: `), yPos);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.text(line.text, x + padding, yPos);
    }
    yPos += lineHeight;
  });

  return y + height + 5;
}

// ============================================================================
// PAGE NUMBERS
// ============================================================================

/**
 * Add page numbers to all pages in the document
 *
 * @param doc - jsPDF instance
 * @param prefix - Prefix text (default: "Page")
 * @param startPage - First page to number (default: 2, skipping title page)
 */
export function addPageNumbers(
  doc: jsPDFWithPlugin,
  prefix = 'Page',
  startPage = 2
): void {
  const { width, height } = getPageDimensions(doc);
  const totalPages = doc.getNumberOfPages();

  for (let i = startPage; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.GRAY_400);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `${prefix} ${i} of ${totalPages}`,
      width / 2,
      height - 10,
      { align: 'center' }
    );
  }
}

// ============================================================================
// DOWNLOAD
// ============================================================================

/**
 * Save and download the PDF
 *
 * @param doc - jsPDF instance
 * @param filename - Filename (without extension)
 */
export function downloadPDF(doc: jsPDF, filename: string): void {
  const sanitizedFilename = filename.replace(/[^a-z0-9-_]/gi, '_');
  const fullFilename = `${sanitizedFilename}.pdf`;
  doc.save(fullFilename);
}

/**
 * Generate a default filename for assessment PDFs
 *
 * @param type - Assessment type (e.g., "maturity", "compliance", "readiness")
 * @param date - Date object (defaults to current date)
 * @returns Formatted filename (without extension)
 */
export function generateAssessmentFilename(type: string, date: Date = new Date()): string {
  const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
  return `${type}-assessment-report-${dateStr}`;
}

/**
 * Generate PDF report for Cloud Security Self-Assessment
 */
export function generateCloudSecurityPDF(
  results: {
    overallPercentage: number;
    overallMaturityLevel: string;
    domainScores: Array<{
      domain: string;
      percentage: number;
      score: number;
      maxScore: number;
      maturityLevel: string;
    }>;
    criticalGaps: string[];
    recommendations: string[];
    cloudProvider: string;
    timestamp: string;
  },
  cloudProvider: string
): void {
  const doc = createAssessmentPDF();

  // Title Page
  addTitlePage(
    doc,
    'Cloud Security',
    'Self-Assessment Report',
    `${cloudProvider.toUpperCase()} Security Posture Evaluation`,
    {
      'Cloud Provider': cloudProvider.toUpperCase(),
      'Assessment Date': formatDate(new Date(results.timestamp)),
      'Overall Score': `${Math.round(results.overallPercentage)}%`,
    },
    'Cloud Security Maturity Level',
    results.overallMaturityLevel.toUpperCase()
  );

  // New page for results
  doc.addPage();
  let yPos = 20;

  // Executive Summary
  yPos = addExecutiveSummary(
    doc,
    [
      { label: 'Overall Score', value: `${Math.round(results.overallPercentage)}%` },
      { label: 'Maturity Level', value: results.overallMaturityLevel.toUpperCase() },
      { label: 'Critical Gaps', value: results.criticalGaps.length },
      { label: 'Recommendations', value: results.recommendations.length },
    ],
    'Executive Summary',
    yPos
  );

  // Check page break
  yPos = checkPageBreak(doc, yPos + 20, 100);

  // Domain Scores Section
  yPos = addSection(doc, 'Security Domain Scores', yPos);

  const domainHeaders = ['Security Domain', 'Score', 'Percentage', 'Maturity'];
  const domainRows = results.domainScores.map((d) => [
    d.domain,
    `${d.score.toFixed(1)}/${d.maxScore.toFixed(1)}`,
    `${Math.round(d.percentage)}%`,
    d.maturityLevel,
  ]);

  yPos = addTable(doc, domainHeaders, domainRows, yPos);

  // Check page break
  yPos = checkPageBreak(doc, yPos + 20, 150);

  // Critical Gaps Section
  if (results.criticalGaps.length > 0) {
    yPos = addSection(doc, `Critical Gaps (${results.criticalGaps.length})`, yPos);

    doc.setFontSize(10);
    doc.setTextColor(...COLORS.GRAY_700);
    doc.setFont('helvetica', 'normal');
    doc.text(
      'The following areas require immediate attention to improve your security posture:',
      20,
      yPos
    );
    yPos += 10;

    yPos = addBulletList(doc, results.criticalGaps.slice(0, 10), 20, yPos, 170);

    if (results.criticalGaps.length > 10) {
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.GRAY_500);
      doc.text(
        `+ ${results.criticalGaps.length - 10} additional critical gap${
          results.criticalGaps.length - 10 !== 1 ? 's' : ''
        }`,
        20,
        yPos
      );
      yPos += 10;
    }
  }

  // Check page break
  yPos = checkPageBreak(doc, yPos + 20, 150);

  // Recommendations Section
  yPos = addSection(doc, 'Top Recommendations', yPos);

  doc.setFontSize(10);
  doc.setTextColor(...COLORS.GRAY_700);
  doc.setFont('helvetica', 'normal');
  doc.text(
    'Based on your assessment, we recommend the following actions to enhance your cloud security:',
    20,
    yPos
  );
  yPos += 10;

  yPos = addBulletList(doc, results.recommendations.slice(0, 10), 20, yPos, 170);

  // Check page break
  yPos = checkPageBreak(doc, yPos + 30, 80);

  // Contact Section
  yPos = addContactSection(doc, yPos);

  // Add page numbers
  addPageNumbers(doc);

  // Disclaimer on last page
  addDisclaimer(doc);

  // Download
  const filename = generateAssessmentFilename(`cloud-security-${cloudProvider}`);
  downloadPDF(doc, filename);
}

// ============================================================================
// PRIORITY INDICATORS
// ============================================================================

/**
 * Get color for priority level
 *
 * @param priority - Priority level
 * @returns RGB color array
 */
export function getPriorityColor(
  priority: 'critical' | 'high' | 'medium' | 'low'
): [number, number, number] {
  switch (priority) {
    case 'critical':
      return COLORS.RED_600;
    case 'high':
      return COLORS.ORANGE_500;
    case 'medium':
      return COLORS.YELLOW_500;
    case 'low':
      return COLORS.GREEN_500;
    default:
      return COLORS.GRAY_500;
  }
}

/**
 * Add a priority indicator dot
 *
 * @param doc - jsPDF instance
 * @param priority - Priority level
 * @param x - X position
 * @param y - Y position
 * @param radius - Dot radius (default: 2)
 */
export function addPriorityDot(
  doc: jsPDF,
  priority: 'critical' | 'high' | 'medium' | 'low',
  x: number,
  y: number,
  radius = 2
): void {
  const color = getPriorityColor(priority);
  doc.setFillColor(...color);
  doc.circle(x, y, radius, 'F');
}

// ============================================================================
// TEXT UTILITIES
// ============================================================================

/**
 * Add wrapped text with proper line breaks
 *
 * @param doc - jsPDF instance
 * @param text - Text to wrap
 * @param x - X position
 * @param y - Y position
 * @param maxWidth - Maximum width for wrapping
 * @param lineHeight - Height per line (default: 5)
 * @returns Final Y position
 */
export function addWrappedText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight = 5
): number {
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
}

/**
 * Add a bulleted list
 *
 * @param doc - jsPDF instance
 * @param items - Array of list items
 * @param x - X position
 * @param y - Y position
 * @param maxWidth - Maximum width for text wrapping
 * @param bulletChar - Bullet character (default: "•")
 * @returns Final Y position
 */
export function addBulletList(
  doc: jsPDF,
  items: string[],
  x: number,
  y: number,
  maxWidth: number,
  bulletChar = '•'
): number {
  let yPos = y;
  const bulletWidth = 5;

  items.forEach((item) => {
    // Draw bullet
    doc.text(bulletChar, x, yPos);

    // Wrap and draw text
    const lines = doc.splitTextToSize(item, maxWidth - bulletWidth - 5);
    doc.text(lines, x + bulletWidth + 5, yPos);
    yPos += lines.length * 5 + 2;
  });

  return yPos;
}

// ============================================================================
// CONTACT SECTION
// ============================================================================

/**
 * Add a standard contact/CTA section
 *
 * @param doc - jsPDF instance
 * @param yPos - Y position
 * @param title - Section title (default: "Need Expert Help?")
 * @param description - Description text
 * @param bullets - Array of bullet points
 * @returns Final Y position
 */
export function addContactSection(
  doc: jsPDF,
  yPos: number,
  title = 'Need Expert Help?',
  description = 'Our compliance and cybersecurity experts can help you:',
  bullets: string[] = [
    'Close critical gaps and achieve compliance faster',
    'Implement security controls aligned with your frameworks',
    'Prepare for successful compliance audits',
  ]
): number {
  const { width } = getPageDimensions(doc);
  const boxHeight = 60;

  // Contact box
  doc.setFillColor(...COLORS.BLUE_600);
  doc.roundedRect(20, yPos, width - 40, boxHeight, 3, 3, 'F');

  let boxY = yPos + 12;

  // Title
  doc.setFontSize(16);
  doc.setTextColor(...COLORS.WHITE);
  doc.setFont('helvetica', 'bold');
  doc.text(title, 25, boxY);

  boxY += 10;

  // Description
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(description, 25, boxY);

  boxY += 6;

  // Bullets
  bullets.forEach((bullet) => {
    doc.text(`• ${bullet}`, 30, boxY);
    boxY += 5;
  });

  boxY += 4;

  // Contact info
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Contact Inventive HQ Today', 25, boxY);

  boxY += 6;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Phone: (866) 903-2097  |  Email: info@inventivehq.com', 25, boxY);

  boxY += 5;
  doc.text('Web: inventivehq.com', 25, boxY);

  return yPos + boxHeight + 10;
}

// ============================================================================
// DISCLAIMER
// ============================================================================

/**
 * Add standard disclaimer text at bottom of page
 *
 * @param doc - jsPDF instance
 * @param customText - Custom disclaimer text (optional)
 */
export function addDisclaimer(
  doc: jsPDF,
  customText?: string
): void {
  const { width, height } = getPageDimensions(doc);
  const yPos = height - 40;

  const defaultText =
    'DISCLAIMER: This assessment provides a general overview based on self-reported information. ' +
    'It does not constitute a formal compliance audit or legal advice. Actual requirements may vary based on your ' +
    'specific circumstances, industry regulations, and contractual obligations. Consult with qualified professionals ' +
    'and legal counsel for formal compliance guidance.';

  doc.setFontSize(8);
  doc.setTextColor(...COLORS.GRAY_500);
  doc.setFont('helvetica', 'italic');

  const disclaimerText = customText || defaultText;
  const wrappedText = doc.splitTextToSize(disclaimerText, width - 40);

  doc.text(wrappedText, 20, yPos);
}
